/**
 * This file contains the Axios instance configuration for API communication.
 * The Axios instance is configured with baseURL and API key from environment variables.
 *
 * Request interceptor doplňuje Bearer token z localStorage. Response
 * interceptor hlídá vypršené přihlášení: místo syrového „Forbidden JWT"
 * předá řízení handleru zaregistrovanému přes session.onSessionExpired
 * (přesměrování na přihlášení s hláškou a návrat zpět).
 */

import axios from 'axios';
import { isAuthFailure, notifySessionExpired, shouldRefreshToken, TOKEN_STORAGE_KEY } from './session';

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'api-key': process.env.VUE_APP_API_KEY,
  },
});

instance.interceptors.request.use(function(config) {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, function(err) {
  return Promise.reject(err);
});

/** Cesty, u kterých 401/403 neznamená vypršelé přihlášení. */
const AUTH_EXEMPT_PATHS = ['/users/login', '/token', '/company-reports'];

function isExempt(config) {
  const url = (config && config.url) || '';
  return AUTH_EXEMPT_PATHS.some((path) => url.startsWith(path));
}

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAuthFailure(error) && !isExempt(error.config)) {
      notifySessionExpired();
    }
    return Promise.reject(error);
  }
);

export const verifyToken = async () => {
  try {
    const response = await instance.get('/token');
    return response.data;
  } catch (error) {
    return false;
  }
};

/**
 * Tichá obnova tokenu, když mu zbývá méně než hodina. Selhání se ignoruje —
 * uživatele nakonec zachytí response interceptor.
 * @returns {Promise<boolean>} True, když byl token obnoven
 */
export const refreshTokenIfNeeded = async () => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!shouldRefreshToken(token)) return false;
  try {
    const response = await instance.post('/users/refresh');
    if (response.data && response.data.token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, response.data.token);
      return true;
    }
  } catch (error) {
    // Nic — token ještě platí, zkusíme to při dalším tiku
  }
  return false;
};

export default instance;
